import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  OnboardingItem,
  Company,
  Settings,
  User,
  Auth,
  Onboarding,
} from "../interfaces";
import { integrationList } from "../components/integrations/constants";

function getLang() {
  try {
    const navLang = navigator?.language;
    if (navLang) return navLang.split("-")[0];
  } catch {}
  return "en";
}

const channelKeys = integrationList
  .filter((i) => i.type === "channel" && !i.devOnly)
  .map((c) => c.key);

const locationKeys = integrationList
  .filter((i) => i.type === "location")
  .map((c) => c.key);

type SettingsCompany = { name: string; id: string };

type AppState = {
  user: User;
  loading: boolean;
  settings: Settings;
  setAppUser: (
    payload: Partial<User> & {
      company?: Company;
      companies?: Company[];
      language?: string;
      role: string;
    },
  ) => void;
  setRole: (role: string) => void;
  setAppLoading: (payload: boolean) => void;
  cleanAppUser: () => void;
  setLanguage: (lang: string) => void;
  setSettings: (payload: any) => void;
  setOnboarded: (onboarded: boolean) => void;
  setTrained: (trained: boolean) => void;
  setOnboarding: (payload: Partial<Onboarding>) => void;
};

const initialUser: User = {
  email: "",
  company: {
    id: "",
    slug: "",
    name: "",
  },
  companies: [],
  logged: false,
  language: getLang(),
  role: "user",
};

const initialSettings: Settings = {
  multiAccount: false,
  loading: true,
  badge: 0,
  onboardingList: [],
  isWaReady: false,
  isVoiceReady: false,
  canSkip: false,
  onboarded: true,
  welcomeCompleted: true,
  events: false,
  playground: {
    id: "",
    contactId: "",
  },
  isAiBookings: false,
  paymentsEligible: false,
  payments: {
    active: false,
    itemsActive: true,
    bookingControlsEnabled: false,
    connectedAccount: "",
    percentage: 1,
  },
  status: {
    trial: false,
    payment: {
      active: true,
      price: "",
      subscription: "",
      email: "",
      endDate: "",
      isTrial: false,
      trialEndsAt: "",
      subscriptionStatus: "",
      pastDue: false,
      retries: 0,
      pendingDowngradeAt: "",
      pendingDowngradePrice: "",
    },
    plan: {
      name: "",
      endDate: "",
      startDate: "",
      totalMessages: 0,
      messageLimit: 0,
      messagesUsed: 0,
      messagesLeft: 1,
      userLimit: 0,
      totalUsers: 0,
      userLeft: 1,
      limitApproaching: false,
      limitReached: false,
    },
  },
  companies: [],
  locations: [],
  company: {
    id: "",
    name: "",
    slug: "",
    website: "",
  } as Company,
  onboarding: {
    loading: false,
    fetchingWebsite: false,
    teachingAgent: false,
    creatingAgent: false,
    error: false,
    message: "",
    website: "",
    agentType: "",
    companyName: "",
    aboutCompany: "",
    // Step 0 (profile) is temporarily disabled while testing a shorter onboarding flow.
    step: 1,
    phoneNumber: "",
    fullName: "",
  },
  role: "admin",
};

const initialLoading = true;

export const useAppStore = create<AppState>()(
  devtools((set, get) => ({
    user: initialUser,
    loading: initialLoading,
    settings: initialSettings,
    setAppUser: (payload) => {
      set(
        (state) => {
          const language = payload.language || getLang();
          const companies = (payload.companies ?? state.user.companies) || [];
          const company = payload.company ?? state.user.company;
          const role = payload.role ?? state.user.role;

          const mappedCompanies: SettingsCompany[] = companies
            .map((c: Company) => ({
              name: c.name || "Undefined",
              id: c.id,
            }))
            .sort((a, b) => a.name.localeCompare(b.name));

          return {
            ...state,
            user: {
              ...state.user,
              ...payload,
              language,
              logged: true,
              role,
            },
            settings: {
              ...state.settings,
              multiAccount: companies.length > 1,
              company: company as any,
              companies: mappedCompanies,
            },
          };
        },
        false,
        "setAppUser",
      );
    },

    setAppLoading: (payload) =>
      set(
        (state) => ({
          ...state,
          loading: payload,
        }),
        false,
        "setAppLoading",
      ),
    setRole: (role: string) =>
      set(
        (state) => ({
          ...state,
          user: {
            ...state.user,
            role,
          },
        }),
        false,
        "setRole",
      ),

    cleanAppUser: () =>
      set(
        (state) => ({
          ...state,
          user: { ...initialUser },
        }),
        false,
        "cleanAppUser",
      ),

    setLanguage: (lang) =>
      set(
        (state) => ({
          ...state,
          user: {
            ...state.user,
            language: lang,
          },
        }),
        false,
        "setLanguage",
      ),

    setSettings: (payload) =>
      set(
        (state) => {
          const hasPayload =
            payload &&
            typeof payload === "object" &&
            Object.keys(payload).length > 0;

          if (!hasPayload) {
            return {
              ...state,
              settings: {
                ...state.settings,
                loading: false,
              },
            };
          }

          const previousAiDone =
            state.settings.onboardingList.find((o) => o.name === "ai")?.done ||
            false;
          const previousChannelDone =
            state.settings.onboardingList.find((o) => o.name === "channel")
              ?.done || false;
          const hasAnyChannelField = channelKeys.some((k) =>
            Object.prototype.hasOwnProperty.call(payload, k),
          );
          const aiDone =
            typeof payload.ai === "boolean" ? payload.ai : previousAiDone;
          const channelDone = hasAnyChannelField
            ? channelKeys.map((k) => payload[k]).some(Boolean)
            : previousChannelDone;

          const onboardingList: OnboardingItem[] = [
            { name: "account", done: true },
            { name: "ai", done: aiDone },
            // {
            //   name: "location",
            //   done: locationKeys.map((k) => payload[k]).some(Boolean),
            // },
            {
              name: "channel",
              done: channelDone,
            },
          ];

          // Derivados del plan (con guardas)
          const planIn = payload?.status?.plan ?? {};
          const safeMessageLimit = Number(planIn.messageLimit ?? 0);
          const safeTotalMessages = Number(planIn.totalMessages ?? 0);
          const safeUserLimit = Number(planIn.userLimit ?? 0);
          const safeTotalUsers = Number(planIn.totalUsers ?? 0);

          const messagesUsedPct =
            safeMessageLimit > 0
              ? (safeTotalMessages / safeMessageLimit) * 100
              : 0;

          const messagesLeft =
            safeMessageLimit > 0
              ? Math.max(0, safeMessageLimit - safeTotalMessages)
              : 0;

          const userLeft =
            safeUserLimit > 0 ? Math.max(0, safeUserLimit - safeTotalUsers) : 0;

          const startDate = planIn.startDate
            ? Date.parse(planIn.startDate)
            : state.settings.status.plan.startDate;

          const endDate = planIn.endDate
            ? Date.parse(planIn.endDate)
            : state.settings.status.plan.endDate;

          const badge = onboardingList.reduce(
            (a, s) => a + (s.done ? 0 : 1),
            0,
          );

          const canSkip = onboardingList
            .filter((o) => ["ai", "channel"].includes(o.name))
            .some((o) => o.done);
          const incomingPayments = payload?.payments || {};
          const itemsActive =
            typeof incomingPayments.itemsActive === "boolean"
              ? incomingPayments.itemsActive
              : true;
          const bookingControlsEnabled =
            typeof incomingPayments.bookingControlsEnabled === "boolean"
              ? incomingPayments.bookingControlsEnabled
              : false;

          return {
            ...state,
            user: payload.company
              ? {
                  ...state.user,
                  company: {
                    ...state.user.company,
                    ...payload.company,
                  } as Company,
                }
              : state.user,
            settings: {
              ...state.settings,
              loading: false,
              company: payload.company
                ? {
                    ...state.settings.company,
                    ...payload.company,
                  }
                : state.settings.company,
              onboarded:
                typeof payload.onboarded === "boolean"
                  ? payload.onboarded
                  : state.settings.onboarded,
              welcomeCompleted:
                typeof payload.welcomeCompleted === "boolean"
                  ? payload.welcomeCompleted
                  : state.settings.welcomeCompleted,
              isWaReady: payload.wa || false,
              isVoiceReady: payload.voice || false,
              isAiBookings: Boolean(payload?.paymentsEligible),
              paymentsEligible: Boolean(payload?.paymentsEligible),
              payments: {
                ...state.settings.payments,
                ...incomingPayments,
                itemsActive,
                bookingControlsEnabled,
              },
              locations: payload.locations || [],
              role: payload.role || "user",
              badge,
              canSkip,
              onboardingList,
              events: payload.events,
              status: {
                ...state.settings.status,
                ...(payload.status || {}),
                plan: {
                  ...state.settings.status.plan,
                  ...(payload.status?.plan || {}),
                  startDate,
                  endDate,
                  totalMessages: safeTotalMessages,
                  messageLimit: safeMessageLimit,
                  messagesUsed: messagesUsedPct,
                  messagesLeft,
                  userLimit: safeUserLimit,
                  totalUsers: safeTotalUsers,
                  userLeft,
                },
              },
              playground: {
                ...state.settings.playground,
                ...(payload.playground || {}),
              },
            },
          };
        },
        false,
        "setSettings",
      ),

    setOnboarded: (onboarded: boolean) =>
      set(
        (state) => ({
          ...state,
          settings: {
            ...state.settings,
            onboarded,
          },
        }),
        false,
        "setOnboarded",
      ),
    setTrained: (trained: boolean) =>
      set(
        (state) => ({
          ...state,
          settings: {
            ...state.settings,
            trained,
          },
        }),
        false,
        "setTrained",
      ),
    setOnboarding: (payload: Partial<Onboarding>) =>
      set(
        (state) => ({
          ...state,
          settings: {
            ...state.settings,
            onboarding: {
              ...state.settings.onboarding,
              ...payload,
            },
          },
        }),
        false,
        "setOnboarding",
      ),
  })),
);

export const getAuthCompany = () => useAppStore.getState().user.company.id;
